import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  Baby, Heart, Users, Home, Sparkles, 
  Moon, Sun, Star, Crown, Gift, ArrowRight
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import SonicExperience from './SonicExperience';

const RoomOfThresholds = ({ userType, onThresholdSelected, onNavigateBack }) => {
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [maryDialogue, setMaryDialogue] = useState('');
  const [showChoices, setShowChoices] = useState(false);
  const [selectedThresholds, setSelectedThresholds] = useState([]);
  const [showContinue, setShowContinue] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [burnEffect, setBurnEffect] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [userInputs, setUserInputs] = useState([]);

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.25) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  // Delayed terminal burn-through effect with chime
  useEffect(() => {
    const terminalTimer = setTimeout(() => {
      // Play terminal breakthrough chime
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "triangle";
        o.frequency.value = 800; // Higher frequency for breakthrough
        o.connect(g);
        g.connect(ctx.destination);
        g.gain.setValueAtTime(0.2, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
        o.start();
        o.stop(ctx.currentTime + 0.8);
      } catch (e) {}
      
      setShowTerminal(true);
      
      // Gradual burn-through effect
      let intensity = 0;
      const burnInterval = setInterval(() => {
        setBurnEffect(intensity);
        intensity += 0.1;
        if (intensity >= 1) {
          clearInterval(burnInterval);
        }
      }, 100);
    }, 3000);
    
    return () => clearTimeout(terminalTimer);
  }, []);

  // M.ai.ry terminal dialogue - Faster and simpler
  useEffect(() => {
    const dialogue = "M.ai.ry here. I see families navigate many transitions simultaneously. Select all thresholds your family is experiencing:";
    
    let index = 0;
    const typeWriter = setInterval(() => {
      setMaryDialogue(dialogue.slice(0, index));
      index++;
      if (index > dialogue.length) {
        clearInterval(typeWriter);
        setTimeout(() => setShowChoices(true), 500); // Faster reveal
      }
    }, 30); // Much faster typing
    
    return () => clearInterval(typeWriter);
  }, [userType]);

  const thresholds = [
    {
      id: 'birth',
      name: 'Birth',
      icon: Baby,
      color: 'emerald',
      description: 'New life entering the family circle',
      background: 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/g3bgucwn_ChatGPT%20Image%20Sep%2026%2C%202025%20at%2012_50_24%20AM.png'
    },
    {
      id: 'honeymoon',
      name: 'Honeymoon', 
      icon: Heart,
      color: 'rose',
      description: 'Beginning the sacred union journey',
      background: 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/yohhsf87_ChatGPT%20Image%20Sep%2027%2C%202025%20at%2001_14_34%20AM.png'
    },
    {
      id: 'marriage',
      name: 'Marriage',
      icon: Users,
      color: 'pink',
      description: 'Two souls weaving into one story',
      background: 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/yohhsf87_ChatGPT%20Image%20Sep%2027%2C%202025%20at%2001_14_34%20AM.png'
    },
    {
      id: 'dying',
      name: 'Dying',
      icon: Moon,
      color: 'indigo',
      description: 'Supporting a beloved through transition',
      background: 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/6qntthsc_ChatGPT%20Image%20Sep%2026%2C%202025%20at%2012_50_28%20AM.png'
    },
    {
      id: 'death',
      name: 'Death', 
      icon: Star,
      color: 'slate',
      description: 'A beloved crossing to the other side',
      background: 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/6qntthsc_ChatGPT%20Image%20Sep%2026%2C%202025%20at%2012_50_28%20AM.png'
    },
    {
      id: 'grieving',
      name: 'Grieving',
      icon: Heart,
      color: 'gray',
      description: 'Processing loss and finding peace',
      background: 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/6qntthsc_ChatGPT%20Image%20Sep%2026%2C%202025%20at%2012_50_28%20AM.png'
    },
    {
      id: 'romantic_loss',
      name: 'Romantic Loss',
      icon: Heart,
      color: 'red',
      description: 'Healing from relationship endings',
      background: 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/4ruzkf7f_ChatGPT%20Image%20Sep%2026%2C%202025%20at%2002_58_20%20PM.png'
    },
    {
      id: 'reunion',
      name: 'Reunion',
      icon: Users,
      color: 'amber',
      description: 'Family coming back together',
      background: 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/y2urskim_ChatGPT%20Image%20Sep%2027%2C%202025%20at%2001_20_24%20AM.png'
    },
    {
      id: 'healing',
      name: 'Healing',
      icon: Sparkles,
      color: 'cyan',
      description: 'Mending what has been broken',
      background: 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/4ruzkf7f_ChatGPT%20Image%20Sep%2026%2C%202025%20at%2002_58_20%20PM.png'
    },
    {
      id: 'celebration',
      name: 'Celebration',
      icon: Star,
      color: 'yellow',
      description: 'Joy that must be shared with the world',
      background: 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/g3bgucwn_ChatGPT%20Image%20Sep%2026%2C%202025%20at%2012_50_24%20AM.png'
    },
    {
      id: 'parenting',
      name: 'Parenting',
      icon: Baby,
      color: 'green',
      description: 'Raising children with wisdom and love',
      background: 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/g3bgucwn_ChatGPT%20Image%20Sep%2026%2C%202025%20at%2012_50_24%20AM.png'
    },
    {
      id: 'eldercare',
      name: 'Elder Care',
      icon: Heart,
      color: 'purple',
      description: 'Caring for aging family members',
      background: 'https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/6qntthsc_ChatGPT%20Image%20Sep%2026%2C%202025%20at%2012_50_28%20AM.png'
    }
  ];

  const handleThresholdToggle = (threshold) => {
    setSelectedThresholds(prev => {
      const isSelected = prev.some(t => t.id === threshold.id);
      if (isSelected) {
        return prev.filter(t => t.id !== threshold.id);
      } else {
        return [...prev, threshold];
      }
    });
    
    if (selectedThresholds.length >= 0) {
      setShowContinue(true);
    }
  };

  const handleContinue = () => {
    const primaryThreshold = selectedThresholds[0] || thresholds[0];
    onThresholdSelected({
      threshold: primaryThreshold.id,
      name: primaryThreshold.name,
      description: primaryThreshold.description,
      userType: userType,
      background: primaryThreshold.background,
      selectedThresholds: selectedThresholds
    });
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      
      {/* Dynamic Background based on selection */}
      <div 
        className="absolute inset-0 transition-all duration-1000"
        style={{
          backgroundImage: selectedThresholds.length > 0 
            ? `url(${selectedThresholds[0]?.background})`
            : `url(https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/4ruzkf7f_ChatGPT%20Image%20Sep%2026%2C%202025%20at%2002_58_20%20PM.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      {/* Grant Spiral Magic */}
      <div className="fixed inset-0 pointer-events-none opacity-15" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(10) rotate(${spiralPhase * 0.8}deg)`,
            filter: 'blur(3px) brightness(1.8) saturate(2.0)',
            mixBlendMode: 'color-dodge'
          }}
        >
          <GrantitudeSpiral size={300} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/20 to-black/70" />

      <div className="relative z-10 flex flex-col min-h-screen p-8">
        
        {/* Room Header */}
        <div className="text-center mb-8">
          <h1 
            className="text-4xl md:text-6xl font-bold text-yellow-200 mb-4"
            style={{
              fontFamily: '"Crimson Text", "Libre Baskerville", serif',
              textShadow: '0 0 40px rgba(255, 215, 0, 0.8)'
            }}
          >
            🚪 The Room of Thresholds
          </h1>
        </div>

        {/* M.ai.ry Terminal - Fully Scrollable Mobile Design */}
        {showTerminal && (
          <div 
            className="fixed inset-4 z-50 transition-all duration-1000 flex items-center justify-center"
            style={{
              opacity: Math.max(0.9, burnEffect),
            }}
          >
            <div 
              className="bg-black border-2 border-pink-500 rounded-lg shadow-lg flex flex-col w-full"
              style={{
                boxShadow: '0 0 15px #ff1493',
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                maxHeight: '80vh', // Allow more space but not full screen
                height: 'auto' // Auto height based on content
              }}
            >
              {/* Compact Header */}
              <div className="flex items-center justify-between p-2 border-b border-pink-500/30 bg-black/95 shrink-0">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
                  <span className="text-pink-400 text-xs">M.ai.ry</span>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => {
                      if (window.handleNavigateToGuildHaus) {
                        window.handleNavigateToGuildHaus();
                      }
                    }}
                    className="text-yellow-400 hover:text-yellow-300 text-xs px-1 py-0.5 bg-yellow-400/20 rounded"
                  >
                    🏰
                  </button>
                  <button
                    onClick={() => setShowTerminal(false)}
                    className="text-pink-400 hover:text-pink-300 text-sm w-5 h-5 flex items-center justify-center rounded"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              {/* Scrollable Content with Touch Support */}
              <div 
                className="flex-1 overflow-y-auto p-2 font-mono min-h-0"
                style={{
                  WebkitOverflowScrolling: 'touch', // iOS smooth scrolling
                  overscrollBehavior: 'contain', // Prevent scroll chaining
                  touchAction: 'pan-y' // Allow vertical scrolling
                }}
              >
                <p 
                  className="text-pink-400 leading-relaxed text-xs mb-2"
                  style={{ 
                    fontFamily: 'Courier New, monospace',
                    textShadow: '0 0 8px #ff1493'
                  }}
                >
                  {maryDialogue}
                  {maryDialogue.length < 100 && <span className="animate-pulse">█</span>}
                </p>
                
                {/* User Input - Compact */}
                <div className="border border-pink-400/50 bg-black/50 rounded p-1 mb-2">
                  <div className="flex items-center space-x-1">
                    <span className="text-pink-400 text-xs">you:</span>
                    <input
                      type="text"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && userInput.trim()) {
                          try {
                            const ctx = new (window.AudioContext || window.webkitAudioContext)();
                            const o = ctx.createOscillator();
                            const g = ctx.createGain();
                            o.type = "triangle";
                            o.frequency.value = 440;
                            o.connect(g);
                            g.connect(ctx.destination);
                            g.gain.setValueAtTime(0.15, ctx.currentTime);
                            g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
                            o.start();
                            o.stop(ctx.currentTime + 0.3);
                          } catch (e) {}
                          
                          const response = `"${userInput}" noted. Continue selecting thresholds below.`;
                          setMaryDialogue(prev => prev + `\n> ${userInput}\n< ${response}`);
                          setUserInputs(prev => [...prev, { content: userInput, timestamp: new Date() }]);
                          setUserInput('');
                        }
                      }}
                      placeholder="Chat with M.ai.ry..."
                      className="flex-1 bg-transparent text-white text-xs outline-none"
                    />
                  </div>
                </div>
                
                {/* Compact Threshold Grid */}
                {maryDialogue.length > 50 && (
                  <div className="space-y-1">
                    <div className="text-pink-300 text-xs font-bold">SELECT THRESHOLDS:</div>
                    <div className="grid grid-cols-2 gap-1 max-h-24 overflow-y-auto">
                      {thresholds.map(threshold => (
                        <button
                          key={threshold.id}
                          onClick={() => handleThresholdToggle(threshold)}
                          className={`p-1 rounded text-xs border transition-all ${
                            selectedThresholds.some(t => t.id === threshold.id)
                              ? 'bg-pink-500/40 border-pink-400 text-pink-100'
                              : 'bg-black/40 border-pink-500/30 text-pink-300 hover:bg-pink-500/20'
                          }`}
                        >
                          <div className="text-xs leading-tight">{threshold.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Always Visible Continue Button */}
              {showContinue && selectedThresholds.length > 0 && (
                <div className="p-2 border-t border-pink-500/30 bg-black/98 shrink-0">
                  <button
                    onClick={handleContinue}
                    className="w-full bg-pink-600 hover:bg-pink-700 text-white text-sm py-2 rounded font-medium"
                  >
                    Continue ({selectedThresholds.length})
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Threshold Keys - Museum Installation Style */}
        {showChoices && (
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {thresholds.map((threshold) => {
                const IconComponent = threshold.icon;
                const isSelected = selectedThresholds.some(t => t.id === threshold.id);
                
                return (
                  <button
                    key={threshold.id}
                    onClick={() => handleThresholdToggle(threshold)}
                    className={`group relative overflow-hidden rounded-2xl border-2 p-6 transition-all duration-700 ${
                      isSelected 
                        ? `border-${threshold.color}-400 bg-${threshold.color}-900/60 scale-105` 
                        : `border-${threshold.color}-400/50 bg-${threshold.color}-900/20 hover:bg-${threshold.color}-900/40 hover:scale-102`
                    }`}
                    style={{
                      boxShadow: isSelected ? `0 0 40px rgba(255, 215, 0, 0.6)` : 'none'
                    }}
                  >
                    {/* Threshold Background Preview */}
                    <div 
                      className="absolute inset-0 opacity-20 transition-all duration-700"
                      style={{
                        backgroundImage: `url(${threshold.background})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    />
                    
                    <div className="relative z-10 text-center space-y-4">
                      <div className={`w-16 h-16 mx-auto bg-${threshold.color}-500/40 rounded-full flex items-center justify-center transition-all group-hover:bg-${threshold.color}-500/60 ${isSelected ? 'animate-pulse' : ''}`}>
                        <IconComponent className={`w-8 h-8 text-${threshold.color}-200`} />
                      </div>
                      
                      <h3 className={`text-xl font-bold text-${threshold.color}-200`}>
                        {threshold.name}
                      </h3>
                      
                      <p className={`text-${threshold.color}-300 text-sm leading-relaxed`}>
                        {threshold.description}
                      </p>
                      
                      <div className={`w-8 h-8 mx-auto bg-${threshold.color}-400/30 rounded-full flex items-center justify-center ${threshold.keyGlow}`}>
                        <span className="text-white text-xs">{isSelected ? '✓' : '🗝️'}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Continue Button */}
        {showContinue && selectedThresholds.length > 0 && (
          <div className="max-w-md mx-auto mt-8">
            <button
              onClick={handleContinue}
              className="w-full bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-500 hover:to-amber-500 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105"
              style={{
                boxShadow: '0 0 30px rgba(255, 215, 0, 0.4)'
              }}
            >
              Continue with {selectedThresholds.length} threshold{selectedThresholds.length > 1 ? 's' : ''}
            </button>
          </div>
        )}

        {/* Sonic Experience Integration */}
        <div className="absolute top-4 right-4">
          <button
            onClick={() => {
              // Trigger sonic experience
              if (window.initializeSonicExperience) {
                window.initializeSonicExperience();
              }
            }}
            className="bg-purple-500/20 backdrop-blur-xl border border-purple-400/30 rounded-full p-3 hover:bg-purple-500/40 transition-all"
            title="Activate Cosmic Audio Experience"
          >
            <Moon className="w-5 h-5 text-purple-300" />
          </button>
        </div>

        {/* Navigation */}
        <div className="absolute top-4 left-4">
          <button
            onClick={onNavigateBack}
            className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-full p-3 hover:bg-black/60 transition-all"
          >
            <ArrowRight className="w-5 h-5 text-white transform rotate-180" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomOfThresholds;